import React, { useState, useEffect } from 'react'

import {
  Card,
  Form,
  Avatar,
  Space,
  Typography,
  Flex,
  Tooltip,
  Rate,
  Button,
  message,
} from 'antd'
import {
  UserOutlined,
  FileUnknownOutlined,
  WhatsAppOutlined,
  EditOutlined,
  LinkOutlined,
  MailOutlined,
  CalendarOutlined,
  PicLeftOutlined,
  IdcardOutlined,
  CopyOutlined,
} from '@ant-design/icons'

import { useContributor } from '~/hooks/Contributors/useContributor'
import { translate } from '~/utils/locale'

import { useCompanies } from '~/hooks/Companies/useCompanies'

import {
  EditButton,
  ShareLinkButton,
} from '~/components/DefaultLayout/Buttons/buttonStyle'
import { createLetterAvatar } from '~/utils/letter'
import HelpTooltip from '~/components/DataManipulation/Misc/HelpTooltip'
import InternalEvaluationModal from '~/components/DataManipulation/Evaluation/InternalEvaluationModal'
import StatusContributor from '~/components/DataManipulation/Status/Contributor'
import { useParams } from 'react-router-dom'

const ContributorDetails: React.FC = () => {
  const { contributorContact, contributorAditionalInfo, setIsEdit, data } =
    useContributor()
  const { selectedCompany } = useCompanies()

  const { contributorId } = useParams<{
    contributorId: string
  }>()

  const [form] = Form.useForm()

  const [documentType, setDocumentType] = useState('')

  useEffect(() => {
    form.setFieldsValue(contributorContact)
    if (contributorContact.person_type) {
      setDocumentType(contributorContact.person_type)
    }
  }, [form, contributorContact])

  const linkBadge = `https://badge.gstor.com.br/company/${selectedCompany}/contributor/${data?.id}/cracha`

  const { Title, Text, Link } = Typography

  return (
    <>
      <Card
        bordered={false}
        title={
          <Title level={5} style={{ marginBottom: 0, fontSize: 18 }}>
            {translate('ContributorDetails.cardTitle')}
          </Title>
        }
        extra={
          <Space>
            <Tooltip title={translate('ContributorTabs.copyTooltip')}>
              <ShareLinkButton
                icon={<CopyOutlined />}
                onClick={() => {
                  navigator.clipboard.writeText(
                    `https://tasks.gstor.com.br/?auth=${selectedCompany},${contributorId}`,
                  )
                  message.success(translate('ContributorTabs.copyMessage'))
                }}
              />
            </Tooltip>
            <Tooltip title="Ver Crachá">
              <Link href={linkBadge} target="_blank">
                <Button type="primary" icon={<IdcardOutlined />}>
                  Crachá
                </Button>
              </Link>
            </Tooltip>
            <EditButton icon={<EditOutlined />} onClick={() => setIsEdit(true)}>
              {translate('ContributorDetails.editButton')}
            </EditButton>
          </Space>
        }
        style={{
          display: 'inline-block',
          width: '100%',
        }}
      >
        <Flex gap={20}>
          {contributorContact.avatar_url ? (
            <Avatar
              shape="square"
              size={120}
              src={contributorContact.avatar_url}
            />
          ) : (
            <Avatar
              shape="square"
              size={120}
              style={{
                fontSize: 30,
                color: '#fff',
                background: 'linear-gradient(135deg, #3C8ACB 0%, #3969B7 100%)',
                borderRadius: 12,
              }}
            >
              {createLetterAvatar(`${contributorContact.name}`)}
            </Avatar>
          )}
          <Flex vertical gap={10} align="stretch">
            <Flex gap={60}>
              <Flex gap={10} style={{ minWidth: 350 }}>
                <Avatar
                  shape="square"
                  icon={
                    <UserOutlined style={{ fontSize: 12, color: '#464545' }} />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    {translate('ContributorDetails.nameLabel')}
                  </Title>
                  {contributorContact.name ? (
                    <Text>{contributorContact.name}</Text>
                  ) : (
                    <Text>Nenhuma informação disponível.</Text>
                  )}
                </div>
              </Flex>

              <Flex gap={10} style={{ minWidth: 350 }}>
                <Avatar
                  shape="square"
                  icon={
                    <MailOutlined style={{ fontSize: 12, color: '#464545' }} />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    {translate('ContributorDetails.emailLabel')}
                  </Title>
                  {contributorContact.email ? (
                    <Text>{contributorContact.email}</Text>
                  ) : (
                    <Text>Nenhuma informação disponível.</Text>
                  )}
                </div>
              </Flex>

              <Flex gap={10} style={{ minWidth: 350 }}>
                <Avatar
                  shape="square"
                  icon={
                    <WhatsAppOutlined
                      style={{ fontSize: 12, color: '#464545' }}
                    />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    Whatsapp
                  </Title>
                  {contributorContact.formattedPhone ? (
                    <Tooltip
                      title={
                        <Space>
                          <LinkOutlined />
                          Ir para o Whatsapp
                        </Space>
                      }
                    >
                      <Link
                        href={`https://wa.me/${contributorContact.phone}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {contributorContact.formattedPhone}
                      </Link>
                    </Tooltip>
                  ) : (
                    <Text>Nenhuma informação disponível.</Text>
                  )}
                </div>
              </Flex>
            </Flex>

            <Flex gap={60}>
              <Flex gap={10} style={{ minWidth: 350 }}>
                <Avatar
                  shape="square"
                  icon={
                    <UserOutlined style={{ fontSize: 12, color: '#464545' }} />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    {translate('ContributorDetails.genderLabel')}
                  </Title>
                  {contributorContact.gender ? (
                    <Text>
                      {contributorContact.gender === 'male'
                        ? translate('ContributorDetails.genderMale')
                        : contributorContact.gender === 'female'
                          ? translate('ContributorDetails.genderFemale')
                          : contributorContact.gender === 'other'
                            ? translate('ContributorDetails.genderOther')
                            : ''}
                    </Text>
                  ) : (
                    <Text>Nenhuma informação disponível.</Text>
                  )}
                </div>
              </Flex>

              <Flex gap={10} style={{ minWidth: 350 }}>
                <Avatar
                  shape="square"
                  icon={
                    <CalendarOutlined
                      style={{ fontSize: 12, color: '#464545' }}
                    />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    {translate('ContributorDetails.birthLabel')}
                  </Title>
                  {contributorContact.formattedBirth ? (
                    <Text>{contributorContact.formattedBirth}</Text>
                  ) : (
                    <Text>Nenhuma informação disponível.</Text>
                  )}
                </div>
              </Flex>

              <Flex gap={10} style={{ minWidth: 350 }}>
                <Avatar
                  shape="square"
                  icon={
                    <UserOutlined style={{ fontSize: 12, color: '#464545' }} />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    {translate('ContributorDetails.personTypeLabel')}
                  </Title>
                  {contributorContact.person_type ? (
                    <Text>
                      {contributorContact.person_type === 'legalPerson'
                        ? translate('ContributorDetails.personTypeLegal')
                        : contributorContact.person_type === 'physicalPerson'
                          ? translate('ContributorDetails.personTypePhysical')
                          : contributorContact.person_type === 'foreign'
                            ? translate('ContributorDetails.personTypeForeign')
                            : ''}
                    </Text>
                  ) : (
                    <Text>Nenhuma informação disponível.</Text>
                  )}
                </div>
              </Flex>
            </Flex>

            <Flex gap={60}>
              {documentType !== '' && (
                <>
                  <Flex gap={10} style={{ minWidth: 350 }}>
                    <Avatar
                      shape="square"
                      icon={
                        <IdcardOutlined
                          style={{ fontSize: 12, color: '#464545' }}
                        />
                      }
                      size="small"
                      style={{ background: '#DADADA' }}
                    />
                    <div>
                      <Title level={5} style={{ marginBottom: 0 }}>
                        {documentType === 'legalPerson' ||
                        documentType === 'physicalPerson' ? (
                          <b>{translate('ContributorDetails.cpfLabel')}</b>
                        ) : (
                          <b>{translate('ContributorDetails.passportLabel')}</b>
                        )}
                      </Title>
                      {!contributorContact.formattedDocument ? (
                        <Text>
                          <Space size="small">
                            <FileUnknownOutlined />
                            N/A
                          </Space>
                        </Text>
                      ) : documentType === 'legalPerson' ||
                        documentType === 'physicalPerson' ? (
                        <Link
                          href="https://servicos.receita.fazenda.gov.br/servicos/cpf/consultasituacao/consultapublica.asp"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {contributorContact.formattedDocument}
                        </Link>
                      ) : (
                        <Text>{contributorContact.formattedDocument}</Text>
                      )}
                    </div>
                  </Flex>

                  {documentType !== 'physicalPerson' && (
                    <Flex gap={10} style={{ minWidth: 350 }}>
                      <Avatar
                        shape="square"
                        icon={
                          <IdcardOutlined
                            style={{ fontSize: 12, color: '#464545' }}
                          />
                        }
                        size="small"
                        style={{ background: '#DADADA' }}
                      />
                      <div>
                        <Title level={5} style={{ marginBottom: 0 }}>
                          {translate('ContributorDetails.cnpjLabel')}
                        </Title>
                        {contributorContact.formattedCnpj ? (
                          <Link
                            href={`https://servicos.receita.fazenda.gov.br/Servicos/cnpjreva/Cnpjreva_Solicitacao.asp?cnpj=${contributorContact.cnpj}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {contributorContact.formattedCnpj}
                          </Link>
                        ) : (
                          <Text>Nenhuma informação disponível.</Text>
                        )}
                      </div>
                    </Flex>
                  )}

                  {documentType === 'physicalPerson' && (
                    <Flex gap={10} style={{ minWidth: 350 }}>
                      <Avatar
                        shape="square"
                        icon={
                          <IdcardOutlined
                            style={{ fontSize: 12, color: '#464545' }}
                          />
                        }
                        size="small"
                        style={{ background: '#DADADA' }}
                      />
                      <div>
                        <Title level={5} style={{ marginBottom: 0 }}>
                          {translate('ContributorDetails.idLabel')}
                        </Title>
                        {contributorContact.rg ? (
                          <Text>{contributorContact.rg}</Text>
                        ) : (
                          <Text>Nenhuma informação disponível.</Text>
                        )}
                      </div>
                    </Flex>
                  )}
                </>
              )}

              <Flex gap={10} style={{ minWidth: 350 }}>
                <Avatar
                  shape="square"
                  icon={
                    <PicLeftOutlined
                      style={{ fontSize: 12, color: '#464545' }}
                    />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    {translate('ContributorDetails.observationLabel')}
                  </Title>
                  {contributorContact?.notes ? (
                    <Text>{contributorContact?.notes}</Text>
                  ) : (
                    <Text>Nenhuma informação disponível.</Text>
                  )}
                </div>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Card>

      <Flex style={{ marginTop: 14 }} align="stretch" gap={10}>
        <Card
          bordered={false}
          title={
            <Space size="small">
              {translate('ContributorTabs.evaluationCardTitle')}
              <HelpTooltip
                title={translate('ContributorTabs.evaluationCardHelp')}
              />
            </Space>
          }
          style={{
            display: 'inline-block',
            width: '100%',
          }}
        >
          <Flex align="center" justify="center" vertical>
            <Title level={4}>{contributorAditionalInfo?.evaluation}</Title>
            <Rate
              disabled
              allowHalf
              value={contributorAditionalInfo?.evaluation}
              style={{ color: '#1E8AFF' }}
            />
          </Flex>
        </Card>

        <Card
          bordered={false}
          title={
            <Space size="small">
              {translate('ContributorTabs.internalEvaluationCardTitle')}
              <HelpTooltip
                title={translate('ContributorTabs.internalEvaluationCardHelp')}
              />
            </Space>
          }
          style={{
            display: 'inline-block',
            width: '100%',
          }}
          extra={<InternalEvaluationModal />}
        >
          <Flex align="center" justify="center" vertical>
            <Title level={4}>
              {contributorAditionalInfo?.internal_evaluation}
            </Title>
            <Rate
              disabled
              allowHalf
              value={contributorAditionalInfo?.internal_evaluation}
              style={{ color: '#1E8AFF' }}
            />
          </Flex>
        </Card>

        <Card
          bordered={false}
          title="Status do Colaborador"
          style={{
            display: 'inline-block',
            width: '100%',
          }}
        >
          <StatusContributor />
        </Card>
      </Flex>
    </>
  )
}

export default ContributorDetails
